"use client";

import { useState } from "react";
import Link from "next/link";
import { LocalDateTime } from "@/components/ui/LocalDateTime";

interface DiaryItem {
  id: string;
  slug: string;
  title: string;
  summary?: string | null;
  thumbnail?: string | null;
  tags: string[];
  publishedAt?: Date | null;
  createdAt: Date;
}

interface Props {
  diaries: DiaryItem[];
  allTags: string[];
}

export function FarmDiaryTagFilter({ diaries, allTags }: Props) {
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const filtered = activeTag
    ? diaries.filter((d) => d.tags.includes(activeTag))
    : diaries;

  return (
    <div>
      {/* 태그 필터 */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          <button
            onClick={() => setActiveTag(null)}
            className={[
              "badge text-sm px-4 py-1.5 cursor-pointer border-2 transition-all",
              activeTag === null
                ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                : "border-[var(--color-border)] text-[var(--color-text-medium)] hover:border-[var(--color-primary-muted)]",
            ].join(" ")}
          >
            전체
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setActiveTag(tag === activeTag ? null : tag)}
              className={[
                "badge text-sm px-4 py-1.5 cursor-pointer border-2 transition-all",
                activeTag === tag
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                  : "border-[var(--color-border)] text-[var(--color-text-medium)] hover:border-[var(--color-primary-muted)]",
              ].join(" ")}
            >
              #{tag}
            </button>
          ))}
        </div>
      )}

      {/* 그리드 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((diary) => (
          <Link
            key={diary.id}
            href={`/farm-diary/${diary.slug}`}
            className="card card-hover block group"
          >
            {/* 썸네일 */}
            <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-light)]">
              <img
                src={diary.thumbnail ?? "/images/hfarm_og.jpg"}
                alt={diary.title}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>

            <div className="p-5 space-y-2">
              {diary.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {diary.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="badge badge-gold text-xs">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              <h3 className="font-serif font-bold text-[var(--color-primary)] leading-snug line-clamp-2">
                {diary.title}
              </h3>

              {diary.summary && (
                <p className="text-sm text-[var(--color-text-light)] line-clamp-2 leading-relaxed">
                  {diary.summary}
                </p>
              )}

              <p className="text-xs text-[var(--color-text-muted)] pt-1">
                <LocalDateTime
                  iso={diary.publishedAt ?? diary.createdAt}
                  variant="date"
                />
              </p>
            </div>
          </Link>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 text-[var(--color-text-light)]">
          해당 태그의 일지가 없습니다.
        </div>
      )}
    </div>
  );
}
